
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface RefundRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
}

export const RefundRequestModal: React.FC<RefundRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  bookingId 
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  

  const handleSubmitRefundRequest = async () => {
    if (!reason.trim()) {
      notify.error("Validation Error", "Please provide a reason for the refund request");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('bookings')
        .update({
          refund_status: 'requested',
          refund_reason: reason.trim(),
          refund_requested_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (error) throw error;

      notify.success("Refund Request Submitted", "Your refund request has been submitted successfully");

      onClose();
    } catch (error) {
      console.error('Error submitting refund request:', error);
      notify.error("Refund Request Failed", "There was an error submitting your refund request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Refund</DialogTitle>
          <DialogDescription>
            Please provide a reason for your refund request. Your practitioner will review and approve the request within 24 hours.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          <Textarea
            placeholder="Reason for refund (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRefundRequest}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
